import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackageMinus } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supplyService } from '@/services/supplyService';
import { invalidateAllSupplyTableCaches } from '@/utils/supplyTableCache';

export function SupplyOnDemandSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;

  const settings = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
  });

  const patch = useMutation({
    mutationFn: (on_demand_negative_stock_as_suggested: boolean) =>
      supplyService.updateOrganizationSettings({ on_demand_negative_stock_as_suggested }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
      invalidateAllSupplyTableCaches();
    },
  });

  const enabled = settings.data?.on_demand_negative_stock_as_suggested ?? false;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <PackageMinus className="w-5 h-5 text-stocker-primary" />
          {t('settings.supplyOnDemandNegativeStock.title')}
        </CardTitle>
        <CardDescription>{t('settings.supplyOnDemandNegativeStock.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('settings.saveError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
              checked={enabled}
              disabled={!canEdit || patch.isPending}
              onChange={(e) => patch.mutate(e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-800">
                {t('settings.supplyOnDemandNegativeStock.label')}
              </span>
              <span className="block text-xs text-gray-500">
                {enabled
                  ? t('settings.supplyOnDemandNegativeStock.onHint')
                  : t('settings.supplyOnDemandNegativeStock.offHint')}
              </span>
            </span>
          </label>
        )}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.supplyOnDemandNegativeStock.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
